/**
 * Avatar initials and tints — one implementation, for every mail surface.
 *
 * These were private to `MailRow`. The compose recipient pills and the
 * suggested-contacts menu draw the same discs, and a second copy of the hash
 * would give the same person a different colour depending on which surface you
 * were looking at — which defeats the entire point of deriving the colour from
 * the name.
 */

/** Avatar initials.
 *
 * The design uses ONE letter for the correspondent and TWO for the mailbox
 * owner — the distinction is deliberate: the sender column is a person, the
 * received-in column is an account, and the wider glyph reads as the heavier
 * of the two at a glance.
 */
export function initials(name, count = 2) {
    return (name || '?')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, count)
        .map((w) => w[0].toUpperCase())
        .join('');
}

/**
 * The one hash. Both palettes below index it, so a person occupies the same
 * slot in each and keeps one hue across every surface.
 *
 * Deriving the slot twice — once per palette — is the bug this function
 * exists to make impossible: the two would agree until someone edited one
 * loop, and then the same sender would be violet in the list and teal in the
 * thread with nothing in the diff to explain it.
 */
function slotFor(name, length) {
    const key = name || '?';
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % length;
}

/** Avatar tints, as the prototype draws them in a LIST.
 *
 * A flat grey disc for every correspondent makes a dense list much harder to
 * scan — the colour is what lets you find the same sender again without
 * reading. Chosen from the name rather than stored, so it is stable for a
 * person across every row and every session without a field to maintain.
 */
const AVATAR_TINTS = [
    'bg-violet-100 text-violet-700',
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-teal-100 text-teal-700',
];

export function avatarTint(name) {
    return AVATAR_TINTS[slotFor(name, AVATAR_TINTS.length)];
}

/**
 * The same identity, as a SOLID fill — what the READER draws.
 *
 * The prototype uses two avatar treatments and the difference is deliberate:
 * a dense list gets pastel discs, because a column of twenty saturated circles
 * vibrates and makes the column harder to read, not easier. The reader has
 * three or four, far apart, at 32px — and at that size and spacing a pastel
 * disc stops reading as an identity at all, so the prototype fills it and puts
 * white text on top.
 *
 * Same slot as `avatarTint`, one hue per person across both.
 */
const AVATAR_SOLIDS = [
    '#6D28D9', // violet
    '#1D4ED8', // blue
    '#047857', // emerald
    '#B45309', // amber
    '#BE123C', // rose
    '#0F766E', // teal
];

export function avatarSolid(name) {
    return AVATAR_SOLIDS[slotFor(name, AVATAR_SOLIDS.length)];
}
